import React, { useEffect, useRef, useCallback } from 'react';
import { X, MapPin, User, Phone, Car, Bell } from 'lucide-react';
import { getRideDestination, getRidePickup } from '../../utils/rideFormat';
import { getAdAttribution } from '../../utils/adSource';

// Notification sound - Base64 encoded short beep
const NOTIFICATION_SOUND = '/notification.mp3';

const playBeep = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.5);
    
    // Second beep
    setTimeout(() => {
      const osc2 = audioContext.createOscillator();
      const gain2 = audioContext.createGain();
      osc2.connect(gain2);
      gain2.connect(audioContext.destination);
      osc2.frequency.value = 1000;
      osc2.type = 'sine';
      gain2.gain.setValueAtTime(0.3, audioContext.currentTime);
      gain2.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      osc2.start(audioContext.currentTime);
      osc2.stop(audioContext.currentTime + 0.5);
    }, 200);
  } catch (e) {
    console.log('Beep failed:', e);
  }
};

const NewRideNotification = ({ 
  ride, 
  isOpen, 
  onClose, 
  onAssignAuto, 
  onAssignManual,
  queueHasDrivers = false
}) => {
  const audioRef = useRef(null);

  const playNotificationSound = useCallback(() => {
    try {
      // Try to play audio
      if (audioRef.current) {
        audioRef.current.currentTime = 0;
        audioRef.current.play().catch(() => {
          // Fallback to Web Audio API beep
          playBeep();
        });
      } else {
        playBeep();
      }
    } catch (e) {
      console.log('Notification sound error:', e);
      playBeep();
    }
  }, []);

  useEffect(() => {
    if (isOpen && ride) {
      // Play notification sound
      playNotificationSound();
    }
  }, [isOpen, ride, playNotificationSound]);

  if (!isOpen || !ride) return null;

  const isFromPassengerApp = ride.passenger_type === 'app' || ride.source === 'passenger_app';
  const isFromWebsite = ride.passenger_type === 'website' || ride.source === 'website';
  const adSource = getAdAttribution(ride);

  return (
    <>
      {/* Hidden audio element */}
      <audio ref={audioRef} src={NOTIFICATION_SOUND} preload="auto" />
      
      {/* Modal Overlay */}
      <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100] p-4 animate-fadeIn">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-slideUp">
          <div
            className="p-4 text-white relative"
            style={{ background: 'var(--brand-red)' }}
          >
            <div className="absolute top-2 right-2">
              <button 
                onClick={onClose}
                className="p-1 hover:bg-white/20 rounded-full transition"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center animate-pulse">
                <Bell className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Porosi e Re!</h2>
                <p className="text-white/80 text-sm">
                  {isFromWebsite ? 'Nga Website' : isFromPassengerApp ? 'Nga Aplikacioni i Pasagjerit' : 'Walk-in'}
                  {adSource ? ` · ${adSource.text}` : ''}
                </p>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-5 space-y-4">
            {/* Ticket Number */}
            <div className="text-center">
              <span className="font-mono text-2xl font-bold text-gray-800">
                {ride.ticket_number || ride.ride_number}
              </span>
            </div>

            {/* Ride Details */}
            <div className="space-y-3">
              {/* From */}
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <MapPin className="w-5 h-5 text-emerald-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium">From</p>
                  <p className="font-semibold text-gray-900">{getRidePickup(ride)}</p>
                </div>
              </div>

              {/* Destination */}
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <MapPin className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-gray-500 uppercase font-medium">Destinacioni</p>
                  <p className="font-semibold text-gray-900">{getRideDestination(ride)}</p>
                </div>
              </div>

              {/* Passenger Info */}
              {(ride.passenger_name || ride.passenger_phone) && (
                <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <User className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 uppercase font-medium">Pasagjeri</p>
                    <p className="font-semibold text-gray-900">
                      {ride.passenger_name || 'Pa emër'}
                    </p>
                    {ride.passenger_phone && (
                      <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                        <Phone className="w-3.5 h-3.5" />
                        {ride.passenger_phone}
                      </p>
                    )}
                    {(ride.passenger_email || ride.customer_email) && (
                      <p className="text-sm text-gray-600 mt-1">
                        {ride.passenger_email || ride.customer_email}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Price & Passengers */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-xs text-green-600 uppercase font-medium">Çmimi</p>
                  {ride.price_determined === false || ride.price_source === 'unpriced' ? (
                    <p className="text-lg font-bold text-green-700">Pa përcaktuar</p>
                  ) : ride.price_eur > 0 ? (
                    <>
                      <p className="text-2xl font-bold text-green-700">€{ride.price_eur}</p>
                      <p className="text-xs text-green-600">
                        {(ride.price_lek || ride.estimated_price || Math.round(ride.price_eur * 100)).toLocaleString()} L
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-2xl font-bold text-green-700">
                        {(ride.price_lek || ride.estimated_price || 0).toLocaleString()} L
                      </p>
                    </>
                  )}
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-xs text-blue-600 uppercase font-medium">Pasagjerë</p>
                  <p className="text-2xl font-bold text-blue-700">{ride.passenger_count}</p>
                  <p className="text-xs text-blue-600">{ride.vehicle_type === 'van' ? 'Van' : 'Sedan'}</p>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2 pt-2">
              {queueHasDrivers && (
                <button
                  onClick={() => onAssignAuto?.(ride.id)}
                  className="w-full py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition"
                  data-testid="notification-auto-assign"
                >
                  <Car className="w-5 h-5" />
                  Cakto Automatikisht (I Pari në Radhë)
                </button>
              )}
              
              <button
                onClick={() => onAssignManual?.(ride)}
                className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-xl font-semibold flex items-center justify-center gap-2 transition"
                data-testid="notification-manual-assign"
              >
                <User className="w-5 h-5" />
                Zgjidh Shoferin Manualisht
              </button>
              
              <button
                onClick={onClose}
                className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-medium transition"
              >
                Shiko Më Vonë
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
      `}</style>
    </>
  );
};

export default NewRideNotification;
